import { useEffect, useMemo, useState } from "react"

import NumberFlow from "@number-flow/react"
import { Flex, Spin, Typography } from "antd"
import cls from "classname"
import { Line } from "react-chartjs-2"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import InfoTooltip from "../../../../Components/InfoTooltip/InfoTooltip"
import Private from "../../../../Components/Private"
import { useAnimatedNumber } from "../../../../hooks/useAnimatedNumber"
import { tooltipLine } from "../../../DashboardPage/components/chartsPlagins"
import { ANALYTICS_SECTION } from "../../redux/analytics.constants"
import { selectSectionData, selectSectionFetching } from "../../redux/analytics.slice"
import {
  buildKpiComparisonOverlay,
  buildKpiDataset,
  formatterFor,
  kpiTrendOptions
} from "../../utils/chartOptions"
import { buildKpiTabs } from "../../utils/kpiTabs"

import styles from "./kpiTabs.module.less"

const { Text } = Typography

// Recover Analytics' headline row: one clickable tab per dispute KPI over a single trend line.
// Two sections in one box: the tabs read kpis/, the chart reads trend/ for the selected tab's
// metric, so switching tabs asks for a new line and leaves the figures where they are.
//
// Deliberately not the Prevent overview's KpiSection: that one derives its tabs from the
// alerts endpoint's blocks and carries that vocabulary with it.

const KpiTab = ({ tab, active, onSelect, comparisonLabel }) => {
  const { t } = useTranslation()
  const animatedValue = useAnimatedNumber(tab.value)

  return (
    <button
      type="button"
      className={cls(styles.tab, { [styles.active]: active })}
      onClick={() => onSelect(tab.key)}
    >
      <span aria-hidden="true" className={styles.underline} />
      <Flex gap={4} align="center">
        <Text className={styles.label} ellipsis={{ tooltip: tab.label }}>
          {tab.label}
        </Text>
        {tab.tooltip && <InfoTooltip title={t(tab.tooltip)} />}
      </Flex>
      <Text className={styles.value}>
        <Private>
          {tab.value == null ? (
            "-"
          ) : (
            <NumberFlow
              value={animatedValue}
              prefix={tab.prefix || ""}
              suffix={tab.suffix || ""}
              format={{ maximumFractionDigits: 0 }}
            />
          )}
        </Private>
      </Text>
      {tab.secondary ? (
        <Text className={styles.secondary}>
          {tab.secondaryPrivate ? <Private>{tab.secondary}</Private> : tab.secondary}
        </Text>
      ) : null}
      {tab.comparison != null ? (
        <Text className={styles.secondary}>
          <Private disabled={!tab.prefix}>
            {`${formatterFor(tab)(tab.comparison)} ${comparisonLabel}`}
          </Private>
        </Text>
      ) : null}
    </button>
  )
}

const KpiTabs = ({ getData }) => {
  const { t } = useTranslation()
  const theme = useSelector((state) => state.nav.theme)
  const kpis = useSelector(selectSectionData(ANALYTICS_SECTION.KPIS))
  const trend = useSelector(selectSectionData(ANALYTICS_SECTION.TREND))
  const trendFetching = useSelector(selectSectionFetching(ANALYTICS_SECTION.TREND))
  const [selectedKey, setSelectedKey] = useState("disputes")

  useEffect(() => {
    getData(ANALYTICS_SECTION.KPIS)
  }, [getData])

  const tabs = useMemo(() => buildKpiTabs(kpis, t), [kpis, t])
  // The win-rate tab leaves the row while nothing is resolved; a selection still pointing at it
  // falls back to the first tab, and the chart follows.
  const selected = tabs.find((tab) => tab.key === selectedKey) ?? tabs[0]
  const metric = selected?.key ?? selectedKey

  useEffect(() => {
    getData(ANALYTICS_SECTION.TREND, { metric })
  }, [getData, metric])

  const comparisonLabel = kpis?.comparison_period?.label || t("previous period")

  const chartOptions = useMemo(
    () =>
      kpiTrendOptions({
        theme,
        title: selected?.label,
        prefix: selected?.prefix,
        suffix: selected?.suffix
      }),
    [theme, selected?.label, selected?.prefix, selected?.suffix]
  )

  const chartData = useMemo(() => {
    const labels = trend?.labels ?? []
    return {
      labels,
      datasets: [
        buildKpiDataset({
          label: selected?.label ?? "",
          data: trend?.data ?? [],
          pointCount: labels.length
        }),
        ...buildKpiComparisonOverlay({
          labels,
          series: trend?.comparison?.data,
          comparisonLabels: trend?.comparison?.labels,
          label: trend?.comparison_period?.label || t("Previous period")
        })
      ]
    }
  }, [trend, selected?.label, t])

  return (
    <div className={styles.section}>
      {tabs.length ? (
        <div className={styles.tabs} style={{ "--kpi-cols": tabs.length }}>
          {tabs.map((tab) => (
            <KpiTab
              key={tab.key}
              tab={tab}
              active={tab.key === selected?.key}
              onSelect={setSelectedKey}
              comparisonLabel={comparisonLabel}
            />
          ))}
        </div>
      ) : null}
      <div className={styles.chart}>
        <Spin spinning={trendFetching}>
          <Line options={chartOptions} data={chartData} height={480} plugins={[tooltipLine]} />
        </Spin>
      </div>
    </div>
  )
}

export default KpiTabs
